import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { MessageCircle, Send } from 'lucide-react';
import { PERMISSIONS } from '@nova/shared';
import { Alert, Button, Drawer, Textarea, Tooltip } from '@/components/ui';
import { useAuthStore } from '@/stores/auth.store';
import { staffChatApi, staffChatKeys } from '../api';
import {
  useSendStaffChatMessage,
  useStaffChatMessages,
  useStaffChatSummary,
} from '../hooks';
import { useStaffChatSound } from '../sound';

/**
 * STAFF-CHAT — reception has the chat on every screen, from the title bar: a
 * floating pill would sit on pagers and legends at the foot of busy screens.
 * The queue and visit pages mount their own launcher for providers too, so the
 * title bar's copy stands aside there instead of doubling up. The calendar's
 * focus mode hides the title bar, so it mounts an `inline` copy in its toolbar.
 */
export function StaffChatDeskLauncher({
  variant = 'titleBar',
}: {
  variant?: 'inline' | 'titleBar';
}) {
  const user = useAuthStore((s) => s.user);
  const canDesk = useAuthStore((s) => s.can)(PERMISSIONS.STAFF_CHAT_DESK);
  const onQueue = Boolean(useMatch('/queue'));
  const onVisit = Boolean(useMatch('/clinical/visits/:id'));
  // Mirrors the API: a provider is never the desk, whatever their roles grant.
  if (!user || user.providerId || !canDesk || onQueue || onVisit) return null;
  return <StaffChatLauncher variant={variant} deskOnly />;
}

/**
 * STAFF-CHAT — mounted by the visit workspace and queue pages (and, for
 * reception, by the title bar). `inline` sits in the visit header beside the
 * reception-note button, because a floating pill there would cover the
 * sidebar's complete-visit action.
 */
export function StaffChatLauncher({
  variant = 'floating',
  deskOnly = false,
}: {
  variant?: 'floating' | 'inline' | 'titleBar';
  deskOnly?: boolean;
}) {
  const { t, i18n } = useTranslation('settings');
  const userId = useAuthStore((s) => s.user?.id) ?? '';
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState('');
  const summary = useStaffChatSummary(open);
  const data = !summary.isError && summary.data?.enabled ? summary.data : null;
  const unread = data?.threads.reduce(
    (total, thread) => total + thread.unread,
    0,
  );
  // Cached data from the other host page must not count as this mount's first poll.
  useStaffChatSound(summary.isFetchedAfterMount ? unread : undefined);
  const providerId =
    data?.role === 'provider' ? (data.threads[0]?.providerId ?? '') : picked;
  const selected = data?.threads.find(
    (thread) => thread.providerId === providerId,
  );

  if (!data?.role || (deskOnly && data.role !== 'desk')) return null;
  return (
    <>
      {variant === 'titleBar' ? (
        <button
          type="button"
          data-testid="staff-chat-launcher"
          aria-label={t('staffChat.title')}
          title={t('staffChat.title')}
          onClick={() => setOpen(true)}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold text-slate-300 transition
                     hover:bg-slate-700 hover:text-white
                     max-lg:h-touch max-lg:w-touch max-lg:justify-center max-lg:px-0"
        >
          <MessageCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="max-lg:hidden">{t('staffChat.title')}</span>
          {!!unread && (
            <span
              data-testid="staff-chat-unread"
              className="rounded-full bg-blue-600 px-1.5 text-[10px] font-bold text-white"
            >
              {unread}
            </span>
          )}
        </button>
      ) : variant === 'inline' ? (
        <Tooltip content={t('staffChat.title')}>
          <Button
            data-testid="staff-chat-launcher"
            aria-label={t('staffChat.title')}
            icon={MessageCircle}
            variant="secondary"
            size="icon"
            className="relative shrink-0"
            onClick={() => setOpen(true)}
          >
            {!!unread && (
              <span
                data-testid="staff-chat-unread"
                className="absolute -end-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] font-bold text-white"
              >
                {unread}
              </span>
            )}
          </Button>
        </Tooltip>
      ) : (
        <Button
          data-testid="staff-chat-launcher"
          aria-label={t('staffChat.title')}
          title={t('staffChat.title')}
          icon={MessageCircle}
          size="icon"
          className="fixed bottom-5 end-5 z-30 h-11 w-11 justify-center rounded-full p-0 shadow-lg max-lg:bottom-3 max-lg:end-3 [&>svg]:h-5 [&>svg]:w-5"
          onClick={() => setOpen(true)}
        >
          {!!unread && (
            <span
              data-testid="staff-chat-unread"
              className="absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold text-blue-700 shadow"
            >
              {unread}
            </span>
          )}
        </Button>
      )}
      <Drawer
        data-testid="staff-chat-panel"
        open={open}
        onOpenChange={setOpen}
        title={t('staffChat.title')}
        width="lg"
        className={data.role === 'desk' ? 'w-[48rem]' : undefined}
        bodyClassName="flex min-h-0 flex-col p-0 max-lg:p-0"
      >
        <p
          data-testid="staff-chat-hint"
          className="shrink-0 border-b bg-blue-50 px-3 py-2 text-xs text-slate-600"
        >
          {t('staffChat.hint')}
        </p>
        <div className="flex min-h-0 flex-1 max-lg:flex-col">
          {data.role === 'desk' && (
            <nav
              aria-label={t('staffChat.choose')}
              className="w-48 shrink-0 overflow-y-auto border-e bg-slate-50 p-2 max-lg:max-h-36 max-lg:w-full max-lg:border-b max-lg:border-e-0"
            >
              {data.threads.map((thread) => (
                <button
                  key={thread.providerId}
                  data-testid={`staff-chat-thread-${thread.providerId}`}
                  aria-pressed={providerId === thread.providerId}
                  className={`mb-1 flex w-full items-center gap-2 rounded px-2 py-2 text-start text-xs hover:bg-blue-50 ${providerId === thread.providerId ? 'bg-blue-100 font-bold' : ''}`}
                  onClick={() => setPicked(thread.providerId)}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {i18n.language === 'en'
                      ? thread.providerName.en || thread.providerName.ar
                      : thread.providerName.ar}
                  </span>
                  {!!thread.unread && (
                    <span
                      data-testid="staff-chat-thread-unread"
                      className="rounded-full bg-blue-600 px-1.5 text-white"
                    >
                      {thread.unread}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          )}
          {selected ? (
            <Conversation
              key={`${userId}:${providerId}`}
              providerId={providerId}
              open={open}
              lastMessageAt={selected.lastMessageAt}
              title={
                data.role === 'provider'
                  ? t('staffChat.reception')
                  : i18n.language === 'en'
                    ? selected.providerName.en || selected.providerName.ar
                    : selected.providerName.ar
              }
            />
          ) : (
            <p className="p-4 text-xs text-slate-500">
              {t('staffChat.choose')}
            </p>
          )}
        </div>
      </Drawer>
    </>
  );
}

function Conversation({
  providerId,
  open,
  title,
  lastMessageAt,
}: {
  providerId: string;
  open: boolean;
  title: string;
  lastMessageAt: string | null;
}) {
  const { t, i18n } = useTranslation('settings');
  const client = useQueryClient();
  const [body, setBody] = useState('');
  const [readError, setReadError] = useState(false);
  const messages = useStaffChatMessages(providerId, open);
  const send = useSendStaffChatMessage(providerId);
  const bottom = useRef<HTMLDivElement>(null);
  const readCursor = useRef<string | null>(null);

  // Wait until the summary has observed this batch, so its incoming sound can
  // run before the receipt clears unread. Unchanged polls need no receipt.
  useEffect(() => {
    if (!open || !messages.isSuccess || messages.isFetching) return;
    const latest = messages.data.at(-1);
    const cursor = latest?.id ?? 'empty';
    if (readCursor.current === cursor) return;
    if (latest && (!lastMessageAt || lastMessageAt < latest.createdAt)) return;
    readCursor.current = cursor;
    let active = true;
    void staffChatApi
      .read(providerId)
      .then(() => {
        if (active) setReadError(false);
        return client.invalidateQueries({
          queryKey: staffChatKeys.all,
          predicate: (query) => query.queryKey.includes('summary'),
        });
      })
      .catch(() => {
        readCursor.current = null;
        if (active) setReadError(true);
      });
    return () => {
      active = false;
    };
  }, [
    client,
    providerId,
    open,
    lastMessageAt,
    messages.isSuccess,
    messages.isFetching,
    messages.dataUpdatedAt,
    messages.data,
  ]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.data]);

  const submit = () => {
    const trimmed = body.trim();
    if (!trimmed || send.isPending || messages.isError) return;
    send.mutate(trimmed, { onSuccess: () => setBody('') });
  };

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col">
      <h2 className="shrink-0 border-b px-3 py-2 text-sm font-bold">{title}</h2>
      <div
        data-testid="staff-chat-messages"
        className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3"
      >
        {messages.isPending && (
          <p className="text-xs text-slate-500">{t('staffChat.loading')}</p>
        )}
        {messages.isError ? (
          <Alert tone="warning">
            <span>{t('staffChat.error')}</span>
            <Button size="sm" onClick={() => void messages.refetch()}>
              {t('staffChat.retry')}
            </Button>
          </Alert>
        ) : (
          messages.data?.map((message) => (
            <article
              key={message.id}
              data-testid="staff-chat-message"
              data-message-id={message.id}
              data-mine={message.mine}
              className={`w-fit max-w-[90%] rounded-lg px-3 py-2 text-xs ${message.mine ? 'ms-auto bg-blue-100' : 'me-auto bg-slate-100'}`}
            >
              <p className="mb-1 text-[10px] font-bold text-slate-500">
                {i18n.language === 'en'
                  ? message.sender.fullNameEn || message.sender.fullNameAr
                  : message.sender.fullNameAr}
              </p>
              <p
                data-testid="staff-chat-message-body"
                dir="auto"
                className="whitespace-pre-wrap break-words"
              >
                {message.body}
              </p>
              <time className="mt-1 block text-[10px] text-slate-400">
                {message.createdAt.slice(11, 16)}
              </time>
            </article>
          ))
        )}
        {messages.isSuccess && messages.data.length === 0 && (
          <p className="text-xs text-slate-500">{t('staffChat.empty')}</p>
        )}
        <div ref={bottom} />
      </div>
      <div className="shrink-0 space-y-2 border-t bg-slate-50 p-3 max-lg:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
        {(send.isError || readError) && (
          <Alert tone="warning">
            {t(send.isError ? 'staffChat.sendError' : 'staffChat.error')}
          </Alert>
        )}
        <Textarea
          data-testid="staff-chat-input"
          aria-label={t('staffChat.input')}
          placeholder={t('staffChat.input')}
          rows={2}
          maxLength={1000}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button
          data-testid="staff-chat-send"
          icon={Send}
          size="sm"
          disabled={!body.trim() || send.isPending || messages.isError}
          loading={send.isPending}
          onClick={submit}
        >
          {t('staffChat.send')}
        </Button>
      </div>
    </section>
  );
}
